import { useEffect, useRef, useState } from 'react'
import { NavLink, Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import {
  Plug, Bot, Key, Upload, CheckCircle, AlertTriangle, XCircle,
  RefreshCw, Trash2, Copy, Eye, EyeOff, Plus, Send, RotateCcw,
  Download, ChevronRight, Zap, Clock, Activity
} from 'lucide-react'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

// ─── Types ────────────────────────────────────────────────────────────────────
interface Integration { id: string; system_id: string; system_name: string; category: string; status: string; last_sync: string | null; records_synced_today: number; error_message: string | null }
interface CatalogItem { id: string; name: string; category: string; tier: string; colour: string; authType: string; desc: string }
interface Message { role: 'user' | 'assistant'; content: string }

// ─── Catalog icon (coloured initial badge) ───────────────────────────────────
function SysIcon({ name, colour, size = 36 }: { name: string; colour: string; size?: number }) {
  const initials = name.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
  return (
    <div className="rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0"
      style={{ width: size, height: size, background: colour, fontSize: size * 0.33 }}>
      {initials}
    </div>
  )
}

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { icon: any; label: string; color: string }> = {
    active:   { icon: CheckCircle, label: 'Connected', color: 'var(--green)' },
    warning:  { icon: AlertTriangle, label: 'Warning', color: 'var(--amber)' },
    error:    { icon: XCircle, label: 'Error', color: 'var(--red)' },
  }
  const c = cfg[status] || cfg.error
  const Icon = c.icon
  return (
    <span className="flex items-center gap-1 text-xs font-medium" style={{ color: c.color }}>
      <Icon size={13} /> {c.label}
    </span>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// HUB — Connected + Available integrations
// ══════════════════════════════════════════════════════════════════════════════
function Hub() {
  const [connected, setConnected] = useState<Integration[]>([])
  const [catalog, setCatalog] = useState<CatalogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [catFilter, setCatFilter] = useState('All')
  const navigate = useNavigate()

  const load = () => {
    Promise.all([api.get('/integrations'), api.get('/integrations/catalog')]).then(([a, b]) => {
      setConnected(a.data.data)
      setCatalog(b.data.data)
    }).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const disconnect = async (id: string) => {
    if (!confirm('Disconnect this integration?')) return
    await api.delete(`/integrations/${id}`)
    load()
  }

  const connectedIds = new Set(connected.map(c => c.system_id))
  const cats = ['All', ...Array.from(new Set(catalog.map(c => c.category)))]
  const available = catalog.filter(c => !connectedIds.has(c.id) && (catFilter === 'All' || c.category === catFilter))

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <div className="space-y-8">
      {/* Connected systems */}
      {connected.length > 0 && (
        <section>
          <h2 className="text-base font-bold mb-4" style={{ color: 'var(--text)' }}>Connected Systems</h2>
          <div className="space-y-3">
            {connected.map(int => {
              const sys = catalog.find(c => c.id === int.system_id)
              return (
                <div key={int.id} className="card p-4">
                  <div className="flex items-center gap-4">
                    <SysIcon name={int.system_name} colour={sys?.colour || '#64748B'} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{int.system_name}</p>
                        <StatusBadge status={int.status} />
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
                        {int.last_sync && <span className="flex items-center gap-1"><Clock size={11} /> Last sync: {new Date(int.last_sync).toLocaleString('en-AU')}</span>}
                        {int.records_synced_today > 0 && <span className="flex items-center gap-1"><Activity size={11} /> {int.records_synced_today} records today</span>}
                        {int.error_message && <span style={{ color: 'var(--red)' }}>{int.error_message}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button className="btn btn-secondary text-xs px-3 py-1.5">Configure</button>
                      <button className="btn btn-secondary text-xs px-3 py-1.5 flex items-center gap-1">
                        <RefreshCw size={12} /> Test
                      </button>
                      <button onClick={() => disconnect(int.id)} className="btn text-xs px-3 py-1.5" style={{ color: 'var(--red)', border: '1px solid var(--red-light)', background: 'var(--red-light)' }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Available integrations */}
      <section>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="text-base font-bold" style={{ color: 'var(--text)' }}>Available Integrations</h2>
          <div className="flex items-center gap-2 flex-wrap">
            {cats.map(cat => (
              <button key={cat} onClick={() => setCatFilter(cat)}
                className="text-xs px-3 py-1.5 rounded-full font-medium transition-all"
                style={{
                  background: catFilter === cat ? 'var(--accent)' : 'var(--bg)',
                  color: catFilter === cat ? 'white' : 'var(--text-secondary)',
                  border: '1px solid var(--border)',
                }}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {available.map(sys => (
            <div key={sys.id} className="card p-4 flex flex-col gap-3 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <SysIcon name={sys.name} colour={sys.colour} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{sys.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--bg)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}>{sys.category}</span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{sys.tier}</span>
                  </div>
                </div>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{sys.desc}</p>
              <button
                onClick={() => navigate('/settings/integrations/aria')}
                className="btn btn-primary text-xs py-2 flex items-center justify-center gap-1.5 mt-auto"
              >
                <Zap size={13} /> Connect with Aria
              </button>
            </div>
          ))}
        </div>
      </section>

      {connected.length === 0 && (
        <div className="card p-8 text-center">
          <Plug size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-semibold mb-1" style={{ color: 'var(--text)' }}>No integrations connected yet</p>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>Connect your payroll or HR system to automatically sync employees and hours worked — improving the accuracy of your safety rates.</p>
          <button onClick={() => navigate('/settings/integrations/aria')} className="btn btn-primary inline-flex items-center gap-2">
            <Bot size={16} /> Chat with Aria to get started
          </button>
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// ARIA — AI integration assistant chat
// ══════════════════════════════════════════════════════════════════════════════
const ARIA_OPENING: Message = {
  role: 'assistant',
  content: `Hi! I'm Aria, your integration assistant. 👋\n\nI can connect HSEQ Intelligence to your existing business systems — payroll, HR, ERP, and more.\n\nOnce connected, I'll automatically pull your employee roster, hours worked, and site data so your safety rates (TRIFR, LTIFR) are always accurate.\n\nWhich system would you like to connect first?`,
}

const QUICK_STARTS = ['KeyPay', 'MYOB Payroll', 'Xero / Xero Payroll', 'Employment Hero', 'SAP S/4HANA', 'Microsoft Dynamics 365', "I'm not sure"]

function Aria() {
  const [messages, setMessages] = useState<Message[]>([ARIA_OPENING])
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>(QUICK_STARTS)
  const [initialising, setInitialising] = useState(true)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api.get('/integrations/aria-session').then(r => {
      const sessionMsgs: Message[] = r.data.messages
      setSessionId(r.data.id)
      if (sessionMsgs.length > 0) {
        setMessages([ARIA_OPENING, ...sessionMsgs])
      }
    }).catch(() => {}).finally(() => setInitialising(false))
  }, [])

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  const send = async (text: string) => {
    if (!text.trim() || loading) return
    const userMsg: Message = { role: 'user', content: text }
    setMessages(m => [...m, userMsg])
    setInput('')
    setLoading(true)
    try {
      const { data } = await api.post('/integrations/aria-chat', { message: text, sessionId })
      setMessages(m => [...m, { role: 'assistant', content: data.reply }])
      setSessionId(data.sessionId)
      setSuggestions(data.suggestedReplies || [])
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: "Sorry, I'm having trouble right now. Please try again in a moment." }])
    } finally {
      setLoading(false)
    }
  }

  const reset = async () => {
    await api.post('/integrations/aria-reset').catch(() => {})
    setMessages([ARIA_OPENING])
    setSuggestions(QUICK_STARTS)
    setSessionId(null)
  }

  if (initialising) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <div className="flex flex-col h-full" style={{ maxHeight: 'calc(100vh - 180px)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: 'var(--purple)' }}>A</div>
          <div>
            <p className="font-bold text-sm" style={{ color: 'var(--text)' }}>Aria</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Integration Assistant · Powered by Claude AI</p>
          </div>
        </div>
        <button onClick={reset} className="btn btn-secondary text-xs flex items-center gap-1.5 px-3 py-1.5">
          <RotateCcw size={13} /> New conversation
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4 pr-1" style={{ minHeight: 0 }}>
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            {msg.role === 'assistant' ? (
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0" style={{ background: 'var(--purple)' }}>A</div>
            ) : (
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0" style={{ background: 'var(--accent)' }}>You</div>
            )}
            <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
              msg.role === 'user' ? 'rounded-tr-sm' : 'rounded-tl-sm'
            }`} style={{
              background: msg.role === 'user' ? 'var(--accent)' : 'var(--surface-raised)',
              color: msg.role === 'user' ? 'white' : 'var(--text)',
              border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ background: 'var(--purple)' }}>A</div>
            <div className="px-4 py-3 rounded-2xl rounded-tl-sm" style={{ background: 'var(--surface-raised)', border: '1px solid var(--border)' }}>
              <div className="flex gap-1 items-center h-5">
                {[0, 1, 2].map(i => (
                  <div key={i} className="w-2 h-2 rounded-full animate-bounce" style={{ background: 'var(--purple)', animationDelay: `${i * 150}ms` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick reply chips */}
      {suggestions.length > 0 && !loading && (
        <div className="flex flex-wrap gap-2 py-3">
          {suggestions.map(s => (
            <button key={s} onClick={() => send(s)}
              className="text-xs px-3 py-1.5 rounded-full font-medium transition-all hover:opacity-80"
              style={{ background: 'var(--purple-light)', color: 'var(--purple)', border: '1px solid var(--purple)' }}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send(input)}
          placeholder="Message Aria…"
          className="flex-1"
          disabled={loading}
        />
        <button onClick={() => send(input)} disabled={!input.trim() || loading} className="btn btn-primary px-4">
          <Send size={16} />
        </button>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// API — REST API credentials + docs
// ══════════════════════════════════════════════════════════════════════════════
const ENDPOINTS = [
  { group: 'Incidents', items: [
    { method: 'GET', path: '/incidents', desc: 'List all incidents' },
    { method: 'POST', path: '/incidents', desc: 'Create a new incident' },
    { method: 'GET', path: '/incidents/:id', desc: 'Get incident by ID' },
    { method: 'PATCH', path: '/incidents/:id', desc: 'Update incident' },
  ]},
  { group: 'Metrics', items: [
    { method: 'GET', path: '/metrics', desc: 'All current safety metrics' },
    { method: 'GET', path: '/metrics/trifr', desc: 'Current TRIFR value' },
  ]},
  { group: 'Workforce', items: [
    { method: 'POST', path: '/workforce/employees', desc: 'Add or update employee' },
    { method: 'POST', path: '/workforce/hours', desc: 'Submit hours worked (improves TRIFR accuracy)' },
  ]},
  { group: 'Corrective Actions', items: [
    { method: 'GET', path: '/actions', desc: 'List corrective actions' },
    { method: 'POST', path: '/actions', desc: 'Create corrective action' },
    { method: 'PATCH', path: '/actions/:id', desc: 'Update status' },
  ]},
  { group: 'Audits', items: [
    { method: 'GET', path: '/audits', desc: 'List audits' },
    { method: 'GET', path: '/audits/:id/report', desc: 'Download audit PDF report' },
  ]},
]

const WEBHOOK_EVENTS = [
  { event: 'incident.created', desc: 'A new incident is logged' },
  { event: 'incident.notifiable_detected', desc: 'Incident flagged as notifiable under WHS Act' },
  { event: 'action.overdue', desc: 'Corrective action passes its due date' },
  { event: 'metric.threshold_breached', desc: 'TRIFR or LTIFR exceeds configured threshold' },
  { event: 'audit.completed', desc: 'An audit is submitted and scored' },
  { event: 'regulatory.deadline_approaching', desc: 'Notifiable incident notification window closing' },
]

const CODE_EXAMPLES: Record<string, string> = {
  JavaScript: `const response = await fetch(
  '${window.location.origin}/api/incidents',
  {
    headers: {
      'X-API-Key': 'your-key-here',
      'Content-Type': 'application/json'
    }
  }
);
const { data } = await response.json();
console.log(data); // array of incidents`,

  Python: `import requests

response = requests.get(
    '${window.location.origin}/api/incidents',
    headers={
        'X-API-Key': 'your-key-here',
        'Content-Type': 'application/json'
    }
)
data = response.json()['data']
print(data)  # list of incidents`,

  cURL: `curl -X GET \\
  '${window.location.origin}/api/incidents' \\
  -H 'X-API-Key: your-key-here' \\
  -H 'Content-Type: application/json'`,
}

function ApiPage() {
  const { user } = useAuthStore()
  const [keyInfo, setKeyInfo] = useState<any>(null)
  const [generatedKey, setGeneratedKey] = useState<string | null>(null)
  const [showKey, setShowKey] = useState(false)
  const [codeTab, setCodeTab] = useState('JavaScript')
  const [expandedGroup, setExpandedGroup] = useState<string | null>('Incidents')
  const [webhooks, setWebhooks] = useState<any[]>([])
  const [wUrl, setWUrl] = useState('')
  const [wEvents, setWEvents] = useState<string[]>([])
  const [copied, setCopied] = useState('')

  const load = () => {
    api.get('/integrations/api-key').then(r => setKeyInfo(r.data))
    api.get('/integrations/webhooks').then(r => setWebhooks(r.data.data))
  }
  useEffect(() => { load() }, [])

  const generateKey = async () => {
    const { data } = await api.post('/integrations/api-key/generate')
    setGeneratedKey(data.key)
    setShowKey(true)
    load()
  }

  const copy = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(''), 2000)
  }

  const addWebhook = async () => {
    if (!wUrl) return
    await api.post('/integrations/webhooks', { url: wUrl, events: wEvents })
    setWUrl(''); setWEvents([])
    load()
  }

  const baseUrl = `${window.location.protocol}//${window.location.hostname}:3000/api`

  return (
    <div className="space-y-8">
      {/* API Key */}
      <section className="card space-y-4">
        <div className="flex items-center gap-2">
          <Key size={18} style={{ color: 'var(--accent)' }} />
          <h2 className="font-bold" style={{ color: 'var(--text)' }}>Your API Credentials</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>API KEY</p>
            {generatedKey ? (
              <div className="p-3 rounded-lg" style={{ background: 'var(--green-light)', border: '1px solid var(--green)' }}>
                <p className="text-xs font-medium mb-2" style={{ color: 'var(--green)' }}>✓ New key generated — copy it now, it won't be shown again</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs flex-1 break-all" style={{ color: 'var(--text)' }}>
                    {showKey ? generatedKey : generatedKey.slice(0, 14) + '•'.repeat(20)}
                  </code>
                  <button onClick={() => setShowKey(s => !s)} className="p-1">{showKey ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                  <button onClick={() => copy(generatedKey, 'key')} className="p-1"><Copy size={14} /></button>
                </div>
                {copied === 'key' && <p className="text-xs mt-1" style={{ color: 'var(--green)' }}>Copied!</p>}
              </div>
            ) : keyInfo?.exists ? (
              <div className="flex items-center gap-3">
                <code className="text-sm" style={{ color: 'var(--text-secondary)' }}>{keyInfo.key_prefix}</code>
                <button onClick={generateKey} className="btn btn-secondary text-xs px-3 py-1.5 flex items-center gap-1">
                  <RefreshCw size={12} /> Regenerate
                </button>
              </div>
            ) : (
              <button onClick={generateKey} className="btn btn-primary flex items-center gap-2">
                <Plus size={14} /> Generate API Key
              </button>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-muted)' }}>BASE URL</p>
            <div className="flex items-center gap-2">
              <code className="text-sm flex-1 px-3 py-2 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                {baseUrl}
              </code>
              <button onClick={() => copy(baseUrl, 'url')} className="btn btn-secondary p-2">
                <Copy size={14} />
              </button>
            </div>
            {copied === 'url' && <p className="text-xs mt-1" style={{ color: 'var(--green)' }}>Copied!</p>}
          </div>
        </div>
      </section>

      {/* Quick start code */}
      <section className="card space-y-4">
        <h2 className="font-bold" style={{ color: 'var(--text)' }}>Quick Start</h2>
        <div className="flex gap-1">
          {Object.keys(CODE_EXAMPLES).map(lang => (
            <button key={lang} onClick={() => setCodeTab(lang)}
              className="text-xs px-3 py-1.5 rounded font-medium"
              style={{ background: codeTab === lang ? 'var(--accent)' : 'var(--bg)', color: codeTab === lang ? 'white' : 'var(--text-secondary)', border: '1px solid var(--border)' }}>
              {lang}
            </button>
          ))}
        </div>
        <div className="relative">
          <pre className="text-xs p-4 rounded-xl overflow-x-auto leading-relaxed" style={{ background: '#0F1923', color: '#E2E8F0', fontFamily: 'var(--font-mono)' }}>
            {CODE_EXAMPLES[codeTab]}
          </pre>
          <button onClick={() => copy(CODE_EXAMPLES[codeTab], 'code')}
            className="absolute top-3 right-3 p-1.5 rounded" style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }}>
            <Copy size={13} />
          </button>
          {copied === 'code' && <span className="absolute top-3 right-10 text-xs" style={{ color: 'var(--green)' }}>Copied!</span>}
        </div>
      </section>

      {/* Endpoint docs */}
      <section className="card space-y-3">
        <h2 className="font-bold" style={{ color: 'var(--text)' }}>Available Endpoints</h2>
        {ENDPOINTS.map(grp => (
          <div key={grp.group} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <button onClick={() => setExpandedGroup(expandedGroup === grp.group ? null : grp.group)}
              className="w-full flex items-center justify-between px-4 py-3 font-medium text-sm"
              style={{ background: 'var(--bg)', color: 'var(--text)' }}>
              {grp.group}
              <ChevronRight size={15} style={{ transform: expandedGroup === grp.group ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {expandedGroup === grp.group && (
              <div className="divide-y" style={{ borderTop: '1px solid var(--border)' }}>
                {grp.items.map(ep => (
                  <div key={ep.path} className="flex items-center gap-4 px-4 py-2.5">
                    <code className="text-xs font-bold w-12 flex-shrink-0" style={{ color: ep.method === 'GET' ? 'var(--green)' : ep.method === 'POST' ? 'var(--blue)' : 'var(--amber)' }}>
                      {ep.method}
                    </code>
                    <code className="text-xs flex-shrink-0" style={{ color: 'var(--text-secondary)' }}>/api{ep.path}</code>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{ep.desc}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </section>

      {/* Webhooks */}
      <section className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-bold" style={{ color: 'var(--text)' }}>Webhooks</h2>
        </div>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Register an HTTPS endpoint to receive real-time event notifications.</p>

        <div className="space-y-2">
          <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>AVAILABLE EVENTS</p>
          <div className="grid md:grid-cols-2 gap-2">
            {WEBHOOK_EVENTS.map(ev => (
              <label key={ev.event} className="flex items-start gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={wEvents.includes(ev.event)} className="mt-0.5 flex-shrink-0"
                  onChange={e => setWEvents(evs => e.target.checked ? [...evs, ev.event] : evs.filter(x => x !== ev.event))} />
                <div>
                  <code style={{ color: 'var(--accent)' }}>{ev.event}</code>
                  <span style={{ color: 'var(--text-muted)' }}> — {ev.desc}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <input value={wUrl} onChange={e => setWUrl(e.target.value)} placeholder="https://your-system.com/webhook" className="flex-1" />
          <button onClick={addWebhook} disabled={!wUrl} className="btn btn-primary flex items-center gap-2 px-4">
            <Plus size={14} /> Register
          </button>
        </div>

        {webhooks.length > 0 && (
          <div className="space-y-2">
            {webhooks.map(wh => (
              <div key={wh.id} className="flex items-center gap-4 p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-mono truncate" style={{ color: 'var(--text)' }}>{wh.url}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{(wh.events || []).join(', ') || 'All events'}</p>
                </div>
                <button onClick={() => api.delete(`/integrations/webhooks/${wh.id}`).then(load)}
                  className="p-1.5 rounded" style={{ color: 'var(--red)' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// FILE IMPORT
// ══════════════════════════════════════════════════════════════════════════════
const IMPORT_TYPES = [
  { type: 'employees', icon: '👥', title: 'Import Employees', desc: 'Upload your employee roster from CSV or Excel. Syncs to HSEQ user accounts and site assignments.' },
  { type: 'hours', icon: '⏱', title: 'Import Hours Worked', desc: 'Upload payroll export with weekly hours. Used to calculate accurate TRIFR and LTIFR rates.' },
  { type: 'incidents', icon: '📋', title: 'Import Historical Incidents', desc: 'Migrate incidents from another system. Upload CSV with historical incident records.' },
  { type: 'assets', icon: '🏗', title: 'Import Asset Register', desc: 'Upload plant and equipment register. Maps to the HSEQ asset module.' },
]

function ImportPage() {
  const [activeType, setActiveType] = useState<string | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<any>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<any>(null)

  const downloadTemplate = async (type: string) => {
    try {
      const res = await api.get(`/integrations/import/template/${type}`, { responseType: 'blob' })
      const contentDisposition = res.headers['content-disposition'] || ''
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      const filename = match ? match[1] : `${type}-template.xlsx`
      const url = URL.createObjectURL(new Blob([res.data]))
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      alert('Failed to download template. Please try again.')
    }
  }

  const handleUpload = async () => {
    if (!file || !activeType) return
    setUploading(true)
    const fd = new FormData()
    fd.append('file', file)
    try {
      const { data } = await api.post(`/integrations/import/${activeType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setPreview(data)
    } catch (err: any) {
      setResult({ error: err.response?.data?.error || 'Upload failed' })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-6">
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
        For systems without APIs — manually import data from spreadsheets. Download the template, fill in your data, then upload.
      </p>

      {/* Import type cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {IMPORT_TYPES.map(it => (
          <div key={it.type} className={`card p-4 cursor-pointer transition-all ${activeType === it.type ? 'ring-2' : ''}`}
            style={{ '--tw-ring-color': 'var(--accent)' } as any}
            onClick={() => { setActiveType(it.type); setFile(null); setPreview(null); setResult(null) }}>
            <div className="flex items-start gap-3">
              <div className="text-2xl">{it.icon}</div>
              <div className="flex-1">
                <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{it.title}</p>
                <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>{it.desc}</p>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={e => { e.stopPropagation(); downloadTemplate(it.type) }}
                className="btn btn-secondary text-xs py-1.5 px-3 flex items-center gap-1">
                <Download size={12} /> Template
              </button>
              {activeType === it.type && (
                <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--accent)' }}>
                  <CheckCircle size={12} /> Selected
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Upload panel */}
      {activeType && (
        <div className="card space-y-4">
          <h3 className="font-semibold" style={{ color: 'var(--text)' }}>
            Upload {IMPORT_TYPES.find(t => t.type === activeType)?.title}
          </h3>

          <div className="border-2 border-dashed rounded-xl p-8 text-center" style={{ borderColor: 'var(--border)' }}>
            {file ? (
              <div>
                <p className="font-medium text-sm" style={{ color: 'var(--text)' }}>{file.name}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{(file.size / 1024).toFixed(1)} KB</p>
                <button onClick={() => { setFile(null); setPreview(null) }} className="text-xs mt-2 underline" style={{ color: 'var(--text-muted)' }}>Remove</button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <Upload size={28} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Drop CSV or Excel file here</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>or click to browse</p>
                <input type="file" className="hidden" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} />
              </label>
            )}
          </div>

          {file && !preview && (
            <button onClick={handleUpload} disabled={uploading} className="btn btn-primary w-full justify-center">
              {uploading ? 'Analysing…' : 'Preview import'}
            </button>
          )}

          {preview && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span style={{ color: 'var(--text)' }}><strong>{preview.rowCount}</strong> rows found</span>
                {preview.unmappedColumns?.length > 0 && (
                  <span style={{ color: 'var(--amber)' }}>⚠ {preview.unmappedColumns.length} columns not matched</span>
                )}
              </div>

              {preview.unmappedColumns?.length > 0 && (
                <div className="p-3 rounded-lg text-xs" style={{ background: 'var(--amber-light)', border: '1px solid var(--amber)' }}>
                  <p className="font-medium mb-1" style={{ color: 'var(--amber)' }}>Unmatched columns:</p>
                  <p style={{ color: 'var(--text)' }}>{preview.unmappedColumns.join(', ')}</p>
                </div>
              )}

              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>PREVIEW (first 5 rows)</p>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg)' }}>
                        {Object.keys(preview.preview[0] || {}).map(k => (
                          <th key={k} className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((row: any, i: number) => (
                        <tr key={i}>
                          {Object.values(row).map((v: any, j: number) => (
                            <td key={j} className="px-3 py-2" style={{ color: 'var(--text)', border: '1px solid var(--border)' }}>{v || '—'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => { setPreview(null); setFile(null) }} className="btn btn-secondary flex-1">Cancel</button>
                <button className="btn btn-primary flex-1 flex items-center justify-center gap-2">
                  <CheckCircle size={15} /> Confirm Import
                </button>
              </div>
            </div>
          )}

          {result?.error && (
            <div className="p-3 rounded-lg text-sm" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>{result.error}</div>
          )}
        </div>
      )}
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT — Tab navigation wrapper
// ══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { path: '/settings/integrations', label: 'Connected Systems', icon: Plug, exact: true },
  { path: '/settings/integrations/aria', label: 'Chat with Aria', icon: Bot },
  { path: '/settings/integrations/api', label: 'API Access', icon: Key },
  { path: '/settings/integrations/import', label: 'File Import', icon: Upload },
]

export default function Integrations() {
  const location = useLocation()

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Integration Hub</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Connect your business systems to keep safety data accurate and automated.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
        {TABS.map(tab => {
          const isActive = tab.exact ? location.pathname === tab.path : location.pathname.startsWith(tab.path)
          const Icon = tab.icon
          return (
            <NavLink key={tab.path} to={tab.path} end={tab.exact}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center"
              style={isActive ? { background: 'white', color: 'var(--accent)', boxShadow: 'var(--shadow-sm)' } : { color: 'var(--text-secondary)' }}>
              <Icon size={15} />
              <span className="hidden sm:inline">{tab.label}</span>
            </NavLink>
          )
        })}
      </div>

      {/* Sub-pages */}
      <Routes>
        <Route index element={<Hub />} />
        <Route path="aria" element={<Aria />} />
        <Route path="api" element={<ApiPage />} />
        <Route path="import" element={<ImportPage />} />
      </Routes>
    </div>
  )
}
